/**
 * The typed API client (ADR-0009, RULES §API-6): request and response types are generated
 * from the server's OpenAPI document (`npm run api:types` → schema.d.ts). Every call goes
 * through `authFetch` (token in memory, one refresh-and-retry on 401).
 */
import createClient from 'openapi-fetch';
import { authFetch } from './auth';
import type { paths } from './schema';

export const api = createClient<paths>({
  baseUrl: typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
  fetch: authFetch,
});

/** An API failure with the server's `{detail}` message (RULES §API-4). */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown) {
    super(ApiError.describe(status, detail));
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }

  static describe(status: number, detail: unknown): string {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0] as { msg?: string; loc?: unknown[] };
      const field = Array.isArray(first.loc) ? first.loc.slice(1).join('.') : '';
      return field ? `${field}: ${first.msg ?? 'invalid'}` : (first.msg ?? `HTTP ${status}`);
    }
    return `HTTP ${status}`;
  }
}

export function isApiError(e: unknown, status?: number): e is ApiError {
  return e instanceof ApiError && (status === undefined || e.status === status);
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** Turn an openapi-fetch result into data or a thrown ApiError (for TanStack Query). */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (error !== undefined || !response.ok) {
    const detail = error && typeof error === 'object' && 'detail' in error ? (error as { detail: unknown }).detail : error;
    throw new ApiError(response.status, detail);
  }
  return data as T;
}
